import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useDarkMode } from "../../contexts/DarkModeContext";
import { useNotificacoes } from "../../hooks/useNotificacoes";
import { useNotificationPermission } from "../../hooks/useNotificationPermission";
import {
  Menu,
  Search,
  Bell,
  BellOff,
  AlertTriangle,
  User,
  LogOut,
  Settings,
  ChevronDown,
  Sparkles,
  Trash2,
  Sun,
  Moon,
} from "lucide-react";

interface NavbarProps {
  onToggleSidebar: () => void;
  onOpenSidebar: () => void;
  sidebarCollapsed: boolean;
}

export default function Navbar({
  onToggleSidebar,
  onOpenSidebar,
  sidebarCollapsed,
}: NavbarProps) {
  const { usuario, logout } = useAuth();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const { notificacoesNaoLidas, totalNaoLidas, deletarNotificacao } =
    useNotificacoes();
  const { needsAttention, isSupported } = useNotificationPermission();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const handleLogout = () => {
    logout();
  };

  return (
    <nav className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md border-r border-gray-200/50 dark:border-gray-700/50 px-4 py-2 relative z-20">
      {/* Background gradient */}
      <div className="absolute inset-0 pointer-events-none" />

      <div className="relative flex items-center justify-between">
        {/* Left Section */}
        <div className="flex items-center space-x-4">
          {/* Mobile Menu Button */}
          <button
            onClick={onOpenSidebar}
            className="lg:hidden p-2 rounded-xl text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100/80 dark:hover:bg-gray-700/80 transition-all duration-150 hover:scale-105"
          >
            <Menu size={20} />
          </button>

          {/* Desktop Sidebar Toggle */}
          <button
            onClick={onToggleSidebar}
            className="hidden lg:block p-2 rounded-xl text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100/80 dark:hover:bg-gray-700/80 transition-all duration-150 hover:scale-105"
          >
            <Menu size={20} />
          </button>
        </div>

        {/* Right Section */}
        <div className="flex items-center space-x-4">
          {/* Theme Toggle Button */}
          <button
            onClick={toggleDarkMode}
            className="p-2 rounded-xl text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100/80 dark:hover:bg-gray-700/80 transition-all duration-150 hover:scale-105"
            title={isDarkMode ? "Modo claro" : "Modo escuro"}
          >
            {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
          </button>
          {/* Push Notification Alert */}
          {isSupported && needsAttention && (
            <Link
              to="/notificacoes/configurar"
              className="p-2 rounded-xl text-orange-500 dark:text-orange-400 hover:text-orange-600 dark:hover:text-orange-300 hover:bg-orange-100/80 dark:hover:bg-orange-900/30 relative transition-all duration-150 hover:scale-105"
              title="Configure as notificações push para receber alertas importantes"
            >
              <BellOff size={20} className="animate-pulse" />
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-orange-500 dark:bg-orange-400 rounded-full" />
            </Link>
          )}

          {/* Notifications */}
          <div className="relative">
            <button
              onClick={() => setNotificationsOpen(!notificationsOpen)}
              className="p-2 rounded-xl text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100/80 dark:hover:bg-gray-700/80 relative transition-all duration-150 hover:scale-105"
            >
              <Bell size={20} />
              {/* Notification Badge */}
              {totalNaoLidas > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-danger-500 to-red-600 rounded-full shadow-lg flex items-center justify-center text-xs text-white font-bold">
                  {totalNaoLidas > 99 ? "99+" : totalNaoLidas}
                </span>
              )}
            </button>

            {/* Notifications Dropdown */}
            {notificationsOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setNotificationsOpen(false)}
                />
                <div className="absolute left-1/10 -translate-x-1/2 md:right-0 md:left-auto md:translate-x-0 mt-3 w-[min(calc(100vw-2rem),20rem)] bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-2xl shadow-strong border border-gray-200/50 dark:border-gray-700/50 z-50 overflow-hidden transform transition-all duration-200 scale-100 opacity-100">
                  <div className="p-3 border-b border-gray-200/50 dark:border-gray-700/50 bg-gradient-to-r from-primary-50/50 to-blue-50/50 dark:from-gray-800/50 dark:to-gray-800/50">
                    <div className="flex items-center space-x-2">
                      <Bell size={18} className="text-primary-600 dark:text-primary-400" />
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        Notificações
                      </h3>
                      <span className="ml-auto px-2 py-1 bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 text-xs rounded-full font-medium">
                        {totalNaoLidas}
                      </span>
                    </div>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {notificacoesNaoLidas.length > 0 ? (
                      notificacoesNaoLidas.map((notificacao) => (
                        <div
                          key={notificacao.id}
                          className="p-4 border-b border-gray-100/50 dark:border-gray-700/50 hover:bg-gray-50/80 dark:hover:bg-gray-700/50 transition-all duration-150 hover:translate-x-1"
                        >
                          <div className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-primary-500 dark:bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-sm text-gray-900 dark:text-gray-100 font-medium">
                                {notificacao.mensagem}
                              </p>
                              {notificacao.ticket_assunto && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  {notificacao.ticket_assunto}
                                </p>
                              )}
                              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                {notificacao.tempo_atras}
                              </p>
                            </div>
                            <div className="flex space-x-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deletarNotificacao(notificacao.id);
                                }}
                                className="p-1 text-danger-600 dark:text-danger-400 hover:text-danger-700 dark:hover:text-danger-300 hover:bg-danger-50 dark:hover:bg-danger-900/30 rounded transition-all duration-150 hover:scale-105"
                                title="Deletar notificação"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                        <Bell
                          size={32}
                          className="mx-auto mb-2 text-gray-300 dark:text-gray-600"
                        />
                        <p className="text-sm">Nenhuma notificação</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                          Você está em dia!
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="p-3 border-t border-gray-200/50 dark:border-gray-700/50 bg-gradient-to-r from-gray-50/50 to-blue-50/50 dark:from-gray-800/50 dark:to-gray-800/50">
                    <div className="flex justify-center">
                      <Link
                        to="/notificacoes"
                        onClick={() => setNotificationsOpen(false)}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline transition-colors font-medium"
                      >
                        Ver todas as notificações
                      </Link>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center space-x-2 p-2 rounded-xl hover:bg-gray-100/80 dark:hover:bg-gray-700/80 transition-all duration-150 hover:scale-102"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-primary-100 to-blue-100 dark:from-primary-900/50 dark:to-blue-900/50 rounded-full flex items-center justify-center shadow-md border-2 border-primary-200/50 dark:border-primary-700/50 overflow-hidden">
                {usuario?.avatar ? (
                  <img
                    src={`${import.meta.env.VITE_API_URL}/img/avatars/${
                      usuario.avatar
                    }`}
                    alt={`Avatar de ${usuario.nome}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-primary-700 dark:text-primary-300 font-bold text-sm">
                    {usuario?.nome?.charAt(0) || "U"}
                  </span>
                )}
              </div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {usuario?.nome}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                  {usuario?.nivel === "dev"
                    ? "Programador"
                    : usuario?.nivel === "admin"
                    ? "Operador"
                    : "Gerente"}
                </p>
              </div>
              <div
                className={`transform transition-transform duration-200 ${
                  userMenuOpen ? "rotate-180" : ""
                }`}
              >
                <ChevronDown size={16} className="text-gray-400 dark:text-gray-500" />
              </div>
            </button>

            {/* User Dropdown */}
            {userMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setUserMenuOpen(false)}
                />
                <div className="absolute right-0 mt-3 w-64 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-2xl shadow-strong border border-gray-200/50 dark:border-gray-700/50 z-50 overflow-hidden transform transition-all duration-200 scale-100 opacity-100">
                  <div className="p-4 border-b border-gray-200/50 dark:border-gray-700/50 bg-gradient-to-r from-primary-50/50 to-blue-50/50 dark:from-gray-800/50 dark:to-gray-800/50">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-primary-200 to-blue-200 dark:from-primary-900/50 dark:to-blue-900/50 rounded-full flex items-center justify-center">
                        <span className="text-primary-700 dark:text-primary-300 font-bold text-lg">
                          {usuario?.nome?.charAt(0) || "U"}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {usuario?.nome}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {usuario?.email}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="py-2">
                    <button
                      onClick={() => {
                        setUserMenuOpen(false);
                        window.location.href = "/usuarios/perfil";
                      }}
                      className="w-full flex items-center px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-primary-50/50 dark:hover:bg-primary-900/30 transition-all duration-150 hover:translate-x-1"
                    >
                      <Settings size={16} className="mr-3 text-primary-600 dark:text-primary-400" />
                      Configurações
                    </button>
                    <Link
                      to="/notificacoes"
                      onClick={() => setUserMenuOpen(false)}
                      className="w-full flex items-center px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-primary-50/50 dark:hover:bg-primary-900/30 transition-all duration-150 hover:translate-x-1"
                    >
                      <Bell size={16} className="mr-3 text-primary-600 dark:text-primary-400" />
                      Notificações
                    </Link>
                  </div>
                  <div className="border-t border-gray-200/50 dark:border-gray-700/50 py-2 bg-gradient-to-r from-gray-50/50 to-red-50/50 dark:from-gray-800/50 dark:to-red-900/20">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center px-4 py-3 text-sm text-danger-600 dark:text-danger-400 hover:bg-danger-50/50 dark:hover:bg-danger-900/30 transition-all duration-150 hover:translate-x-1"
                    >
                      <LogOut size={16} className="mr-3" />
                      Sair
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Decorative elements */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-500 via-blue-500 to-indigo-500 dark:from-primary-400 dark:via-blue-400 dark:to-indigo-400 opacity-50" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-gray-700 to-transparent" />
    </nav>
  );
}
